import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/StatusChip";
import ReviewSummaryBadge from "@/components/ReviewSummaryBadge";
import SafetyOnlyBadge from "@/components/SafetyOnlyBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_META, projectTypeLabel } from "@/lib/constants";
import { Plus, Search, ArrowUpDown } from "lucide-react";

// Sort options for the project list. Kept client-side (list is capped
// at 500 rows in the API anyway, and PMs frequently switch sort while
// scanning — no reason to round-trip). "newest" preserves the
// backend's default `created_at desc` order so a fresh load matches
// what everyone's used to.
const SORT_KEY = "f2_projects_sort";
const SORT_OPTIONS = {
  newest:      { label: "Newest first" },
  project_az:  { label: "Project name (A–Z)" },
  site_az:     { label: "Site name (A–Z)" },
  site_number: { label: "Site number (A–Z)" },
  customer_az: { label: "Customer (A–Z)" },
  status:      { label: "Status" },
};
// Status order used by the "status" sort so a PM triaging inflight
// work sees the active buckets first and closed jobs last, instead
// of an alphabetical scramble (Approved before Draft, etc.).
const STATUS_ORDER = [
  "draft",
  "safety_complete",
  "field_complete",
  "testing_complete",
  "photo_complete",
  "ready_for_review",
  "approved",
  "closed",
];
const _statusRank = (s) => {
  const i = STATUS_ORDER.indexOf(s || "");
  return i === -1 ? STATUS_ORDER.length : i;
};
const _cmpStr = (a, b) =>
  (a || "").toLowerCase().localeCompare((b || "").toLowerCase(), undefined, { numeric: true });

export default function ProjectListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  // Persist the PM's sort choice per-device — a PM who prefers
  // grouping by project name shouldn't have to reset the picker
  // every time they open the app.
  const [sort, setSort] = useState(() => {
    const saved = localStorage.getItem(SORT_KEY);
    return saved && SORT_OPTIONS[saved] ? saved : "newest";
  });

  const fetchProjects = async () => {
    const params = {};
    if (q) params.q = q;
    if (status && status !== "all") params.status = status;
    const { data } = await api.get("/projects", { params });
    setProjects(data);
  };

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line
  }, [status]);

  useEffect(() => {
    localStorage.setItem(SORT_KEY, sort);
  }, [sort]);

  // Sort is applied client-side over the already-fetched list.
  // `newest` short-circuits — the API returns `created_at desc` so
  // we just leave the order alone. All other sorts operate on a
  // fresh copy so we never mutate the underlying state array.
  const sortedProjects = useMemo(() => {
    if (sort === "newest") return projects;
    const rows = [...projects];
    switch (sort) {
      case "project_az":
        // Fallback to site_name when project_name is blank so those
        // rows still land somewhere sensible instead of clumping
        // at the top under empty strings.
        rows.sort((a, b) => _cmpStr(a.project_name || a.site_name, b.project_name || b.site_name));
        break;
      case "site_az":
        rows.sort((a, b) => _cmpStr(a.site_name, b.site_name));
        break;
      case "site_number":
        rows.sort((a, b) => _cmpStr(a.site_number, b.site_number));
        break;
      case "customer_az":
        rows.sort((a, b) => _cmpStr(a.customer, b.customer));
        break;
      case "status":
        // Bucket by workflow position, then break ties by newest
        // within each bucket so a PM sees the freshest inflight
        // work at the top of each status group.
        rows.sort((a, b) => {
          const s = _statusRank(a.status) - _statusRank(b.status);
          if (s !== 0) return s;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        break;
      default:
        break;
    }
    return rows;
  }, [projects, sort]);

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-slate-400 font-display">All</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight" data-testid="projects-heading">Projects</h1>
        </div>
        <Button
          className="bg-red-700 hover:bg-red-600 font-display tracking-wide"
          onClick={() => navigate("/projects/new")}
          data-testid="projects-new"
        >
          <Plus className="w-4 h-4 mr-1.5" /> NEW
        </Button>
      </div>

      <Card className="bg-slate-900 border-slate-800 mb-4">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search site number, name, customer, foreman..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchProjects()}
              className="pl-9 bg-slate-950 border-slate-700"
              data-testid="projects-search"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full sm:w-56 bg-slate-950 border-slate-700" data-testid="projects-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Client-side sort — lets a PM group by project (so all
              jobs for the same project cluster together regardless
              of site), by site, by customer, or by workflow status.
              Choice persists per-device. */}
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger
              className="w-full sm:w-52 bg-slate-950 border-slate-700"
              data-testid="projects-sort-select"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SORT_OPTIONS).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`projects-sort-opt-${k}`}>
                  {v.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={fetchProjects} className="bg-blue-700 hover:bg-blue-600" data-testid="projects-search-btn">Search</Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="p-0">
          {sortedProjects.length === 0 ? (
            <div className="p-8 text-center text-slate-400" data-testid="projects-empty">No projects match your filters.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {sortedProjects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="w-full text-left p-4 hover:bg-slate-800/50 transition-colors flex items-center gap-3"
                  data-testid={`project-list-row-${p.id}`}
                >
                  <div className="font-mono text-xs text-slate-400 w-24 truncate">{p.site_number}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold truncate flex items-center gap-2 flex-wrap">
                      <span className="truncate">{p.site_name || p.project_name}</span>
                      {/* Project name pill — only when BOTH names are
                          present AND distinct. If site_name is blank
                          we already fall back to project_name in the
                          title above; showing a pill of the same text
                          would just duplicate the label on the row. */}
                      {p.site_name && p.project_name && p.project_name !== p.site_name && (
                        <span
                          className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-950/60 border border-blue-800 text-blue-200 max-w-[16rem] truncate"
                          title={p.project_name}
                          data-testid={`project-list-name-${p.id}`}
                        >
                          {p.project_name}
                        </span>
                      )}
                      <SafetyOnlyBadge projectType={p.project_type} size="xs" />
                      <ReviewSummaryBadge summary={p.review_summary} />
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {p.customer} · {p.foreman || "—"} · {projectTypeLabel(p.project_type)}
                    </div>
                  </div>
                  <StatusChip status={p.status} />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
